/**
 * Authenticated Fetch Utility
 * 
 * Centralizes all API calls with:
 * - Automatic auth header injection from localStorage token
 * - Safe JSON response parsing (prevents "body stream already read" errors)
 * - Consistent error handling
 * - Convenience methods for GET, POST, PUT, PATCH, DELETE
 * 
 * Usage:
 *   import { api } from '../utils/api';
 * 
 *   // GET
 *   const { ok, data, error } = await api.get('/users/me');
 * 
 *   // POST with JSON body
 *   const { ok, data } = await api.post('/tracks', { title: 'My Track' });
 * 
 *   // POST with FormData (file upload)
 *   const formData = new FormData();
 *   formData.append('file', file);
 *   const { ok, data } = await api.upload('/upload/image', formData);
 * 
 *   // Raw fetch with auth (for custom needs)
 *   const { ok, data } = await api.fetch('/endpoint', { method: 'POST', body: ... });
 */

import { API } from '../config/api';

function getToken() {
  return localStorage.getItem('token');
}

function authHeaders() {
  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function safeParseResponse(response) {
  const status = response.status;
  const ok = response.ok;

  try {
    const text = await response.text();
    let data = null;

    if (text && text.trim()) {
      try {
        data = JSON.parse(text);
      } catch {
        return { ok: false, status, data: null, error: 'Invalid JSON response' };
      }
    }

    return {
      ok,
      status,
      data,
      error: ok ? null : (data?.detail || data?.message || data?.error || `Request failed (${status})`)
    };
  } catch (readError) {
    return { ok: false, status, data: null, error: readError.message || 'Failed to read response' };
  }
}

/**
 * Core authenticated fetch
 * Adds auth headers automatically, parses JSON safely
 */
async function authenticatedFetch(path, options = {}) {
  const url = path.startsWith('http') ? path : `${API}${path.startsWith('/') ? '' : '/'}${path}`;

  const headers = {
    ...authHeaders(),
    ...(options.headers || {})
  };

  try {
    const response = await fetch(url, { ...options, headers });
    return await safeParseResponse(response);
  } catch (networkError) {
    return {
      ok: false,
      status: 0,
      data: null,
      error: networkError.message || 'Network error'
    };
  }
}

/**
 * GET request
 * @param {string} path - API path (e.g., '/users/me')
 * @param {object} params - Optional query parameters
 */
async function get(path, params = null) {
  let url = path;
  if (params) {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v != null)
    ).toString();
    if (qs) url += `?${qs}`;
  }
  return authenticatedFetch(url, { method: 'GET' });
}

/**
 * POST request with JSON body
 */
async function post(path, body = null) {
  const options = { method: 'POST' };
  if (body !== null) {
    options.headers = { 'Content-Type': 'application/json' };
    options.body = JSON.stringify(body);
  }
  return authenticatedFetch(path, options);
}

/**
 * PUT request with JSON body
 */
async function put(path, body = null) {
  const options = { method: 'PUT' };
  if (body !== null) {
    options.headers = { 'Content-Type': 'application/json' };
    options.body = JSON.stringify(body);
  }
  return authenticatedFetch(path, options);
}

/**
 * PATCH request with JSON body
 */
async function patch(path, body = null) {
  const options = { method: 'PATCH' };
  if (body !== null) {
    options.headers = { 'Content-Type': 'application/json' };
    options.body = JSON.stringify(body);
  }
  return authenticatedFetch(path, options);
}

/**
 * DELETE request
 */
async function del(path, body = null) {
  const options = { method: 'DELETE' };
  if (body !== null) {
    options.headers = { 'Content-Type': 'application/json' };
    options.body = JSON.stringify(body);
  }
  return authenticatedFetch(path, options);
}

/**
 * File upload (FormData) - does NOT set Content-Type (browser sets boundary)
 */
async function upload(path, formData) {
  return authenticatedFetch(path, {
    method: 'POST',
    body: formData
  });
}

export const api = {
  fetch: authenticatedFetch,
  get,
  post,
  put,
  patch,
  delete: del,
  upload,
  API_URL: API
};

export { API, authenticatedFetch, authHeaders };
export default api;
